import { Eye, EyeOff } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconCheckdactiveBlue from 'src/assets/icons/IconCheckdactiveBlue.svg';
import IconCheckdDeactive from 'src/assets/icons/IconCheckdDeactive.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import { PasswordFormData, PasswordErrorMsg } from 'src/interface/common';

interface UpdatePasswordFormProps {
    formData: PasswordFormData;
    errorMsg: PasswordErrorMsg;
    isStrongPassword: boolean;
    isLoading: boolean;
    handleSubmit: (event: React.FormEvent) => void;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const UpdatePasswordForm: React.FC<UpdatePasswordFormProps> = ({ formData, errorMsg, isLoading, handleSubmit, handleInputChange }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Password validation functions matching the rules from generatePassword.ts
    const hasMinLength = (password: string) => /.{8,}/.test(password);
    const hasUppercase = (password: string) => /[A-Z]/.test(password);
    const hasLowercase = (password: string) => /[a-z]/.test(password);
    const hasNumber = (password: string) => /[0-9]/.test(password);
    const hasSpecialChar = (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // Check if all password requirements are met
    const isPasswordValid = (password: string) => hasMinLength(password) && hasUppercase(password) && hasLowercase(password) && hasNumber(password) && hasSpecialChar(password);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
            <div>
                <InputText
                    label={t('Auth.Password_Manager.Set_Password')}
                    labelClassName="block mb-1.5"
                    inputType={showPassword ? 'text' : 'password'}
                    inputName="password"
                    size="md"
                    className={`pr-[36px] peer ${errorMsg?.password ? ' !border-[#FDA29B] ' : ' !border-[#D0D5DD] '}`}
                    placeholder={t('Auth.Password_Manager.Enter_Password')}
                    value={formData.password}
                    onChangeFunc={handleInputChange}
                    icon={
                        <div
                            className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer peer-placeholder-shown:pointer-events-none peer-placeholder-shown:text-gray400"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </div>
                    }
                />
            </div>
            <div>
                <InputText
                    label={t('Auth.Password_Manager.Confirm_Password')}
                    labelClassName="block mb-1.5"
                    inputType={showConfirmPassword ? 'text' : 'password'}
                    inputName="confirm_password"
                    className={`pr-[36px] peer ${errorMsg?.confirm_password ? ' !border-[#FDA29B] ' : ' !border-[#D0D5DD] '}`}
                    placeholder={t('Auth.Password_Manager.Enter_your_confirm_password')}
                    value={formData.confirm_password}
                    onChangeFunc={handleInputChange}
                    icon={
                        <div
                            className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer peer-placeholder-shown:pointer-events-none peer-placeholder-shown:text-gray400"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </div>
                    }
                    size="md"
                    errorText={errorMsg?.server_error || errorMsg?.confirm_password}
                />
            </div>

            <ButtonCmp
                type="submit"
                size="lg"
                disabled={isLoading || !formData.password || !formData.confirm_password || !isPasswordValid(formData.password) || formData.password !== formData.confirm_password}
                className="btn_primary_black w-full"
                loading={isLoading}
            >
                {t('Auth.Password_Manager.Continue')}
            </ButtonCmp>
            <div className="flex flex-col justify-start mb-4">
                <div className={`flex gap-1.5 items-center text-[13px] font-normal ${hasMinLength(formData.password) ? 'text-primary' : 'text-grayLight600'}`}>
                    {hasMinLength(formData.password) ? (
                        <img src={IconCheckdactiveBlue} alt="" className={`w-[15px] h-[15px]`} />
                    ) : (
                        <img src={IconCheckdDeactive} alt="" className={`w-[15px] h-[15px]`} />
                    )}
                    <span>Has at least 8 characters?</span>
                </div>
                <div className={`flex items-center mt-2 text-[13px] font-normal gap-1.5 ${hasUppercase(formData.password) ? 'text-primary' : 'text-grayLight600'}`}>
                    {hasUppercase(formData.password) ? (
                        <img src={IconCheckdactiveBlue} alt="" className={`w-[15px] h-[15px]`} />
                    ) : (
                        <img src={IconCheckdDeactive} alt="" className={`w-[15px] h-[15px]`} />
                    )}
                    <span>Has one uppercase letter?</span>
                </div>
                <div className={`flex items-center mt-2 text-[13px] font-normal gap-1.5 ${hasLowercase(formData.password) ? 'text-primary' : 'text-grayLight600'}`}>
                    {hasLowercase(formData.password) ? (
                        <img src={IconCheckdactiveBlue} alt="" className={`w-[15px] h-[15px]`} />
                    ) : (
                        <img src={IconCheckdDeactive} alt="" className={`w-[15px] h-[15px]`} />
                    )}
                    <span>Has one lowercase letter?</span>
                </div>
                <div className={`flex items-center mt-2 text-[13px] font-normal gap-1.5 ${hasNumber(formData.password) ? 'text-primary' : 'text-grayLight600'}`}>
                    {hasNumber(formData.password) ? <img src={IconCheckdactiveBlue} alt="" className={`w-[15px] h-[15px]`} /> : <img src={IconCheckdDeactive} alt="" className={`w-[15px] h-[15px]`} />}
                    <span>Has one digit?</span>
                </div>
                <div className={`flex items-center mt-2 text-[13px] font-normal gap-1.5 ${hasSpecialChar(formData.password) ? 'text-primary' : 'text-grayLight600'}`}>
                    {hasSpecialChar(formData.password) ? (
                        <img src={IconCheckdactiveBlue} alt="" className={`w-[15px] h-[15px]`} />
                    ) : (
                        <img src={IconCheckdDeactive} alt="" className={`w-[15px] h-[15px]`} />
                    )}
                    <span>Has one special character?</span>
                </div>
            </div>
        </form>
    );
};

export default UpdatePasswordForm;
